import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authApi } from '@/api/auth'
import { meApi } from '@/api/me'
import type { LoginView, UserProfile } from '@/types/api'
import { clearAccessToken, setAccessToken, tokenState } from '@/utils/token'

const CLIENT_ID_STORAGE_KEY = 'uc.clientId'

/**
 * 登录/切换时的可选目标应用（`client_id`）。
 *
 * 只接受 `client_id` 而不是应用主键：主键是自增数字，前端拿着它去请求就等于
 * 把"换个数字试试"的扫描入口开给使用者。
 */
export type ClientIdInput = string | null | undefined

export const useUserStore = defineStore('user', () => {
  const profile = ref<UserProfile | null>(null)
  const logging = ref(false)

  const accessToken = computed(() => tokenState.accessToken.value)
  const permVersion = computed(() => tokenState.permVersion.value)
  const authenticated = computed(() => accessToken.value !== null)

  async function loadProfile(): Promise<UserProfile | null> {
    try {
      profile.value = await meApi.profile()
    } catch {
      profile.value = null
    }
    return profile.value
  }

  /**
   * 应用登录结果（ADR-11）：只有 `tokens` 非空才算登录完成。
   *
   * 返回原始结果而不是布尔值，是为了让调用方（登录页）能区分
   * 「进 MFA」与「必须改密」—— 这两条后续流程完全不同，压成一个布尔量只会让页面写出一堆 if。
   */
  /**
   * 落库登录结果。
   *
   * 服务端会把"登录时选定的应用"回写在令牌里，这里直接把它同步到应用上下文：
   * 换账号登录时不留上一个账号的应用标识，菜单才不会按旧账号的可见范围去拉。
   */
  async function applyLoginResult(result: LoginView): Promise<LoginView> {
    if (result.tokens) {
      setAccessToken(result.tokens.accessToken, result.tokens.expiresIn, result.tokens.permVersion,
        result.tokens.clientId ?? null)
      if (result.tokens.clientId) {
        storeClientId(result.tokens.clientId)
      } else {
        localStorage.removeItem(CLIENT_ID_STORAGE_KEY)
      }
      await loadProfile()
    }
    return result
  }

  async function login(identifier: string, password: string, clientId?: ClientIdInput): Promise<LoginView> {
    logging.value = true
    try {
      return await applyLoginResult(
        await authApi.passwordLogin({
          identifier,
          password,
          channel: 'WEB',
          clientId: clientId ?? null,
        }),
      )
    } finally {
      logging.value = false
    }
  }

  async function loginByCode(target: string, code: string, clientId?: ClientIdInput): Promise<LoginView> {
    logging.value = true
    try {
      return await applyLoginResult(
        await authApi.codeLogin({
          target,
          code,
          channel: 'WEB',
          clientId: clientId ?? null,
        }),
      )
    } finally {
      logging.value = false
    }
  }

  async function verifyMfa(pendingToken: string, code: string): Promise<LoginView> {
    return applyLoginResult(await authApi.mfaLogin(pendingToken, code))
  }

  async function resetPassword(pendingToken: string, newPassword: string): Promise<LoginView> {
    return applyLoginResult(await authApi.resetPassword(pendingToken, newPassword))
  }

  /**
   * 页面刷新后的静默续期。
   *
   * access 只存在内存里，刷新即丢失；只要 Cookie 里的 `uc_rt` 还有效就能换回一张新的，
   * 用户不会察觉。失败（没有 Cookie / 已过期 / 已被登出）一律按未登录处理 ——
   * 这里不区分原因，区分了也没法让"没登录的人"看到不同的东西。
   */
  async function bootstrap(): Promise<boolean> {
    if (tokenState.accessToken.value) {
      return true
    }
    try {
      const pair = await authApi.refresh()
      setAccessToken(pair.accessToken, pair.expiresIn, pair.permVersion, pair.clientId)
      await loadProfile()
      return true
    } catch {
      return false
    }
  }

  /**
   * 切换**会话**所在应用：服务端按新应用重签令牌，这里同步本地状态。
   *
   * 失败不做本地降级 —— 应用上下文只能来自令牌，擅自留在本地会让
   * "界面在 B、令牌在 A"这种不一致长期存在，最后表现为菜单加载 403。
   *
   * 注意与控制台的「管理对象选择」区分（stores/app.ts）：重签会把令牌里的登录应用改掉，
   * 控制台菜单与按钮权限随之漂到另一个应用上，因此管理后台的应用切换器**不应该**走这里。
   */
  async function switchApp(clientId: string): Promise<void> {
    const pair = await authApi.switchApp(clientId)
    setAccessToken(pair.accessToken, pair.expiresIn, pair.permVersion, pair.clientId)
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      clearAccessToken()
      // 应用上下文是"登录之后才有的东西"，登出必须一并清掉，否则下一个登录者继承上一个的选择
      localStorage.removeItem(CLIENT_ID_STORAGE_KEY)
      profile.value = null
    }
  }

  return {
    profile,
    logging,
    accessToken,
    permVersion,
    authenticated,
    login,
    loginByCode,
    verifyMfa,
    resetPassword,
    bootstrap,
    switchApp,
    logout,
    loadProfile,
  }
})

export function readStoredClientId(): string | null {
  return localStorage.getItem(CLIENT_ID_STORAGE_KEY)
}

export function storeClientId(clientId: string): void {
  localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId)
}
